import { openDB } from 'idb';

const DB_NAME = 'telcavoip-offline';
const DB_VERSION = 1;
const STORES = {
  DRAFT_EMAILS: 'draft-emails',
  PENDING_ACTIONS: 'pending-actions',
  CACHED_EMAILS: 'cached-emails'
};

// Initialize IndexedDB
export const initOfflineDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Store for draft emails written offline
      if (!db.objectStoreNames.contains(STORES.DRAFT_EMAILS)) {
        db.createObjectStore(STORES.DRAFT_EMAILS, { keyPath: 'id', autoIncrement: true });
      }
      // Store for pending actions (send, delete, mark read, etc.)
      if (!db.objectStoreNames.contains(STORES.PENDING_ACTIONS)) {
        db.createObjectStore(STORES.PENDING_ACTIONS, { keyPath: 'id', autoIncrement: true });
      }
      // Store for cached emails for offline viewing
      if (!db.objectStoreNames.contains(STORES.CACHED_EMAILS)) {
        const store = db.createObjectStore(STORES.CACHED_EMAILS, { keyPath: 'email_id' });
        store.createIndex('account_id', 'account_id');
      }
    }
  });
};

// Save draft email offline
export const saveDraftOffline = async (draft) => {
  const db = await initOfflineDB();
  const draftWithMeta = {
    ...draft,
    createdAt: new Date().toISOString(),
    status: 'draft'
  };
  return db.add(STORES.DRAFT_EMAILS, draftWithMeta);
};

// Get all offline drafts
export const getOfflineDrafts = async () => {
  const db = await initOfflineDB();
  return db.getAll(STORES.DRAFT_EMAILS);
};

// Delete offline draft
export const deleteOfflineDraft = async (id) => {
  const db = await initOfflineDB();
  return db.delete(STORES.DRAFT_EMAILS, id);
};

// Queue action for when back online
export const queueOfflineAction = async (action) => {
  const db = await initOfflineDB();
  const actionWithMeta = {
    ...action,
    queuedAt: new Date().toISOString(),
    status: 'pending'
  };
  return db.add(STORES.PENDING_ACTIONS, actionWithMeta);
};

// Get all pending actions
export const getPendingActions = async () => {
  const db = await initOfflineDB();
  return db.getAll(STORES.PENDING_ACTIONS);
};

// Mark action as completed
export const completeAction = async (id) => {
  const db = await initOfflineDB();
  return db.delete(STORES.PENDING_ACTIONS, id);
};

// Cache emails for offline viewing
export const cacheEmailsOffline = async (emails) => {
  const db = await initOfflineDB();
  const tx = db.transaction(STORES.CACHED_EMAILS, 'readwrite');
  for (const email of emails) {
    await tx.store.put(email);
  }
  await tx.done;
};

// Get cached emails
export const getCachedEmails = async () => {
  const db = await initOfflineDB();
  return db.getAll(STORES.CACHED_EMAILS);
};

// Clear cached emails
export const clearCachedEmails = async () => {
  const db = await initOfflineDB();
  const tx = db.transaction(STORES.CACHED_EMAILS, 'readwrite');
  await tx.store.clear();
  await tx.done;
};

// Sync manager - process pending actions when online
export class OfflineSyncManager {
  constructor(apiClient) {
    this.apiClient = apiClient;
    this.isSyncing = false;
    this.onSyncComplete = null;
    this.onSyncError = null;
    
    // Listen for online event
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.sync());
    }
  }

  async sync() {
    if (this.isSyncing || !navigator.onLine) return;
    
    this.isSyncing = true;
    const results = { success: [], failed: [] };

    try {
      // Process pending actions
      const pendingActions = await getPendingActions();
      
      for (const action of pendingActions) {
        try {
          await this.processAction(action);
          await completeAction(action.id);
          results.success.push(action);
        } catch (error) {
          console.error('Failed to process action:', action, error);
          results.failed.push({ action, error: error.message });
        }
      }

      // Send draft emails
      const drafts = await getOfflineDrafts();
      for (const draft of drafts.filter(d => d.status === 'queued')) {
        try {
          await this.apiClient.post('/api/emails/send', {
            account_id: draft.account_id,
            to: draft.to,
            cc: draft.cc || [],
            subject: draft.subject,
            body: draft.body,
            is_html: draft.is_html || false
          });
          await deleteOfflineDraft(draft.id);
          results.success.push({ type: 'send_email', draft });
        } catch (error) {
          console.error('Failed to send draft:', draft, error);
          results.failed.push({ draft, error: error.message });
        }
      }

      if (this.onSyncComplete) {
        this.onSyncComplete(results);
      }
    } catch (error) {
      console.error('Sync error:', error);
      if (this.onSyncError) {
        this.onSyncError(error);
      }
    } finally {
      this.isSyncing = false;
    }

    return results;
  }

  async processAction(action) {
    switch (action.type) {
      case 'mark_read':
        return this.apiClient.patch(`/api/emails/${action.emailId}`, { is_read: true });
      case 'mark_unread':
        return this.apiClient.patch(`/api/emails/${action.emailId}`, { is_read: false });
      case 'star':
        return this.apiClient.patch(`/api/emails/${action.emailId}`, { is_starred: true });
      case 'unstar':
        return this.apiClient.patch(`/api/emails/${action.emailId}`, { is_starred: false });
      case 'delete':
        return this.apiClient.delete(`/api/emails/${action.emailId}`);
      case 'send_email':
        return this.apiClient.post('/api/emails/send', action.payload);
      default:
        console.warn('Unknown action type:', action.type);
    }
  }

  // Queue email for sending when online
  async queueEmailForSending(emailData) {
    if (navigator.onLine) {
      // Send immediately
      return this.apiClient.post('/api/emails/send', emailData);
    } else {
      // Queue for later
      const draft = {
        ...emailData,
        status: 'queued',
        queuedAt: new Date().toISOString()
      };
      await saveDraftOffline(draft);
      return { queued: true, message: 'Email salvata e verrà inviata quando tornerai online' };
    }
  }
}

// Check if online
export const isOnline = () => {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
};

// Get pending sync count
export const getPendingSyncCount = async () => {
  const actions = await getPendingActions();
  const drafts = await getOfflineDrafts();
  const queuedDrafts = drafts.filter(d => d.status === 'queued');
  return actions.length + queuedDrafts.length;
};
